/**
 * Componente StatCard padrão para todo o sistema
 * Design minimalista e profissional com suporte a tema escuro
 * Reutilizável em todos os módulos
 */

import React from 'react';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: 'positive' | 'negative' | 'neutral' | null;
  trendLabel?: string;
  emphasis?: 'default' | 'high';
  hideTrendIndicator?: boolean;
  valueClassName?: string;
  className?: string;
}

export function StatCard({
  title,
  value,
  subtitle,
  trend,
  trendLabel,
  emphasis = 'default',
  hideTrendIndicator = false,
  valueClassName = '',
  className = '',
}: StatCardProps) {
  const isHighEmphasis = emphasis === 'high';

  return (
    <div
      className={`relative rounded-xl p-6 border ${isHighEmphasis ? 'border-primary/40 bg-gradient-to-b from-primary/5 to-card shadow-[0_0_0_1px_hsl(var(--primary)/0.15)]' : 'border-border bg-card'} ${className}`}
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</h3>

          {!hideTrendIndicator && trend && trend !== 'neutral' && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="relative">
                <div
                  className={`absolute inset-0 rounded-full animate-ping ${
                    trend === 'positive'
                      ? 'bg-green-500'
                      : trend === 'negative'
                        ? 'bg-red-500'
                        : 'bg-gray-400'
                  } opacity-75`}
                />
                <div
                  className={`relative w-1.5 h-1.5 rounded-full ${
                    trend === 'positive'
                      ? 'bg-green-500'
                      : trend === 'negative'
                        ? 'bg-red-500'
                        : 'bg-gray-400'
                  }`}
                />
              </div>
              <span
                className={`text-xs font-medium ${
                  trend === 'positive'
                    ? 'text-green-600 dark:text-green-400'
                    : trend === 'negative'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {trendLabel ||
                  (trend === 'positive' ? 'Positivo' : trend === 'negative' ? 'Negativo' : 'Neutro')}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-1">
          <div
            className={`${isHighEmphasis ? 'text-4xl md:text-[2.6rem]' : 'text-3xl'} font-light italic text-card-foreground tracking-tight ${valueClassName}`}
          >
            {value}
          </div>

          {subtitle && <p className="text-sm text-muted-foreground/90 font-light">{subtitle}</p>}
        </div>
      </div>

      <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
    </div>
  );
}
